import { useState } from "react";
import { Check, Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { SavedPhoneNumber } from "@/types/admin";
import { toast } from "@/hooks/use-toast";

// Default phone numbers for testing
const DEFAULT_PHONE_NUMBERS: SavedPhoneNumber[] = [
  { id: "1", name: "Your Phone", number: "+12025551234", isDefault: true },
  { id: "2", name: "Shaddy's Phone", number: "+12025555678" },
];

interface SavedPhoneNumbersProps {
  onSelectNumber: (number: string) => void;
  selectedNumber: string;
}

const SavedPhoneNumbers = ({ onSelectNumber, selectedNumber }: SavedPhoneNumbersProps) => {
  // Load from localStorage or use defaults
  const [phoneNumbers, setPhoneNumbers] = useState<SavedPhoneNumber[]>(() => {
    const saved = localStorage.getItem("testCallPhoneNumbers");
    return saved ? JSON.parse(saved) : DEFAULT_PHONE_NUMBERS;
  });
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingNumber, setEditingNumber] = useState<SavedPhoneNumber | null>(null);
  const [newNumber, setNewNumber] = useState<Partial<SavedPhoneNumber>>({
    name: "",
    number: "",
    isDefault: false
  });

  // Save to localStorage whenever phone numbers change
  const savePhoneNumbers = (numbers: SavedPhoneNumber[]) => {
    setPhoneNumbers(numbers);
    localStorage.setItem("testCallPhoneNumbers", JSON.stringify(numbers));
  };

  const handleAddNumber = () => {
    if (!newNumber.name || !newNumber.number) {
      toast({
        title: "Missing information",
        description: "Please provide both a name and phone number",
        variant: "destructive"
      });
      return;
    }

    // Format the phone number
    const formattedNumber = formatPhoneNumber(newNumber.number);
    
    const updatedNumbers = [...phoneNumbers];
    
    // If this is the first number or marked as default, set all others to non-default
    if (newNumber.isDefault || phoneNumbers.length === 0) {
      updatedNumbers.forEach(num => num.isDefault = false);
    }
    
    // Add the new number
    updatedNumbers.push({
      id: Date.now().toString(),
      name: newNumber.name,
      number: formattedNumber,
      isDefault: newNumber.isDefault || phoneNumbers.length === 0
    });
    
    savePhoneNumbers(updatedNumbers);
    setIsAddDialogOpen(false);
    setNewNumber({ name: "", number: "", isDefault: false });
    
    toast({
      title: "Phone number added",
      description: `${newNumber.name} has been added to your saved numbers`
    });
  };

  const handleEditNumber = () => {
    if (!editingNumber || !editingNumber.name || !editingNumber.number) {
      return;
    }

    // Format the phone number
    const formattedNumber = formatPhoneNumber(editingNumber.number);
    
    const updatedNumbers = phoneNumbers.map(num => {
      if (num.id === editingNumber.id) {
        return {
          ...editingNumber,
          number: formattedNumber
        };
      }
      
      // If this number is set as default, unset others
      if (editingNumber.isDefault && num.id !== editingNumber.id) {
        return { ...num, isDefault: false };
      }
      
      return num;
    });
    
    savePhoneNumbers(updatedNumbers);
    setIsEditDialogOpen(false);
    setEditingNumber(null);
    
    toast({
      title: "Phone number updated",
      description: `${editingNumber.name} has been updated`
    });
  };

  const handleDeleteNumber = (id: string) => {
    const numberToDelete = phoneNumbers.find(num => num.id === id);
    if (!numberToDelete) return;
    
    const updatedNumbers = phoneNumbers.filter(num => num.id !== id);
    
    // If the deleted number was default, set a new default
    if (numberToDelete.isDefault && updatedNumbers.length > 0) {
      updatedNumbers[0].isDefault = true;
    }
    
    savePhoneNumbers(updatedNumbers);
    
    toast({
      title: "Phone number removed",
      description: `${numberToDelete.name} has been removed from your saved numbers`
    });
  };

  const handleSetDefault = (id: string) => {
    const updatedNumbers = phoneNumbers.map(num => ({
      ...num,
      isDefault: num.id === id
    }));
    
    savePhoneNumbers(updatedNumbers);
    
    // Get the default number
    const defaultNumber = updatedNumbers.find(num => num.id === id);
    if (defaultNumber) {
      onSelectNumber(defaultNumber.number);
    }
  };

  // Helper function to format phone numbers
  const formatPhoneNumber = (number: string): string => {
    // Remove all non-digit characters
    const digitsOnly = number.replace(/\D/g, '');
    
    // Ensure it has the + prefix if it doesn't
    if (!number.startsWith('+')) {
      // For US numbers, add +1 if not already there
      if (digitsOnly.length === 10) {
        return `+1${digitsOnly}`;
      }
      // Otherwise just add the + prefix
      return `+${digitsOnly}`;
    }
    
    return number;
  };

  // Select a phone number from the list
  const handleSelectNumber = (number: SavedPhoneNumber) => {
    onSelectNumber(number.number);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium">Saved Phone Numbers</h3>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => {
            setNewNumber({ name: "", number: "", isDefault: false });
            setIsAddDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Number
        </Button>
      </div>
      
      <div className="rounded-md border">
        {phoneNumbers.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <p>No saved phone numbers</p>
            <p className="text-xs mt-1">Add a phone number to get started</p>
          </div>
        ) : (
          <div className="divide-y">
            {phoneNumbers.map((phoneNumber) => (
              <div 
                key={phoneNumber.id} 
                className={`p-3 flex items-center justify-between hover:bg-muted cursor-pointer ${
                  selectedNumber === phoneNumber.number ? 'bg-muted' : ''
                }`}
                onClick={() => handleSelectNumber(phoneNumber)}
              >
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id={`number-${phoneNumber.id}`}
                    checked={selectedNumber === phoneNumber.number}
                    onCheckedChange={() => handleSelectNumber(phoneNumber)}
                  />
                  <div className="ml-2">
                    <p className="text-sm font-medium">{phoneNumber.name}</p>
                    <p className="text-xs text-muted-foreground">{phoneNumber.number}</p>
                  </div>
                  {phoneNumber.isDefault && (
                    <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {!phoneNumber.isDefault && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(phoneNumber.id);
                      }}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingNumber(phoneNumber);
                      setIsEditDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteNumber(phoneNumber.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Add New Number Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input 
                id="name" 
                placeholder="E.g., My Phone"
                value={newNumber.name}
                onChange={(e) => setNewNumber({ ...newNumber, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="number">Phone Number</Label>
              <Input 
                id="number" 
                placeholder="+1 (202) 555-1234"
                value={newNumber.number}
                onChange={(e) => setNewNumber({ ...newNumber, number: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Enter in any format, it will be automatically formatted
              </p>
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="default" 
                checked={newNumber.isDefault}
                onCheckedChange={(checked) => 
                  setNewNumber({ ...newNumber, isDefault: !!checked })
                }
              />
              <Label htmlFor="default">Set as default number</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNumber}>
              Add Number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Number Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Phone Number</DialogTitle>
          </DialogHeader>
          {editingNumber && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input 
                  id="edit-name" 
                  value={editingNumber.name}
                  onChange={(e) => setEditingNumber({ ...editingNumber, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-number">Phone Number</Label>
                <Input 
                  id="edit-number" 
                  value={editingNumber.number}
                  onChange={(e) => setEditingNumber({ ...editingNumber, number: e.target.value })}
                />
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="edit-default" 
                  checked={editingNumber.isDefault}
                  onCheckedChange={(checked) => 
                    setEditingNumber({ ...editingNumber, isDefault: !!checked })
                  }
                />
                <Label htmlFor="edit-default">Set as default number</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditNumber}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedPhoneNumbers;
